import { type KeyboardEvent, useRef } from 'react';

import type { IIRevResultState, IResult } from '../../../interface/state/IRev';
import { CopyValue } from '../../utils/copy';
import { fmtNum } from '../child-agent/utils';
import { getPartyColor } from '../upload-result/party-colors';
import { asPercent, type IDerivedKpis, type IVsrTab, sortedParties, type VsrTab } from './utils';

// ── Glyphs ──
export const WarnGlyph = () => (
	<svg
		viewBox='0 0 20 20'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M10 6.7v3.6M10 13.4h.01M8.6 2.6 1.7 14.5c-.6 1 .1 2.3 1.4 2.3h13.8c1.3 0 2-1.3 1.4-2.3L11.4 2.6c-.6-1.1-2.2-1.1-2.8 0Z'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

export const DocIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M6 2.5h8L19 7v13a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 20V4A1.5 1.5 0 0 1 6 2.5Z'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinejoin='round'
		/>
		<path
			d='M14 2.5V7h5M8.5 12h7M8.5 15.5h7'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinecap='round'
		/>
	</svg>
);

export const MegaphoneIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M4 9v6h3l8 4V5L7 9H4Zm13 .5a3 3 0 0 1 0 5'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

// ── Sticky tab bar (mirrors the Agent Coverage drawer `.ca-tabs`) ──
interface IVsrTabsProps {
	tabs: IVsrTab[];
	active: VsrTab;
	onChange: (tab: VsrTab) => void;
}

export const VsrTabs = ({ tabs, active, onChange }: IVsrTabsProps) => {
	const refs = useRef<(HTMLButtonElement | null)[]>([]);

	const focusTab = (index: number) => {
		const next = (index + tabs.length) % tabs.length;
		refs.current[next]?.focus();
		onChange(tabs[next].key);
	};

	const onKeyDown = (e: KeyboardEvent, index: number) => {
		switch (e.key) {
			case 'ArrowRight':
			case 'ArrowDown':
				e.preventDefault();
				focusTab(index + 1);
				break;
			case 'ArrowLeft':
			case 'ArrowUp':
				e.preventDefault();
				focusTab(index - 1);
				break;
			case 'Home':
				e.preventDefault();
				focusTab(0);
				break;
			case 'End':
				e.preventDefault();
				focusTab(tabs.length - 1);
				break;
			default:
				break;
		}
	};

	return (
		<div
			className='ca-tabs'
			role='tablist'
			aria-label='Result views'
		>
			{tabs.map((tab, index) => {
				const isActive = tab.key === active;
				return (
					<button
						key={tab.key}
						ref={(el) => (refs.current[index] = el)}
						type='button'
						role='tab'
						id={`vsr-tab-${tab.key}`}
						aria-selected={isActive}
						aria-controls={`vsr-panel-${tab.key}`}
						tabIndex={isActive ? 0 : -1}
						className={`ca-tabs__tab ${isActive ? 'is-active' : ''}`}
						onClick={() => onChange(tab.key)}
						onKeyDown={(e) => onKeyDown(e, index)}
					>
						<span className='ca-tabs__label-full'>{tab.label}</span>
						<span className='ca-tabs__label-short'>{tab.short}</span>
					</button>
				);
			})}
		</div>
	);
};

// ── Persistent entity context header ──
interface IContextField {
	label: string;
	value: string;
	mono?: boolean;
	copy?: boolean;
}

export const EntityContextHeader = ({
	eyebrow,
	title,
	status,
	fields,
}: {
	eyebrow: string;
	title: string;
	status?: string | null;
	fields: IContextField[];
}) => (
	<div className='vsr-context'>
		<div className='vsr-context__head'>
			<div className='vsr-context__heading'>
				<span className='vsr-context__eyebrow'>{eyebrow}</span>
				<h3 className='vsr-context__title'>{title}</h3>
			</div>
			{status ? <span className='vsr-context__status'>{status}</span> : null}
		</div>
		<div className='vsr-context__grid'>
			{fields
				.filter((f) => f.label)
				.map((f) => (
					<div
						className='vsr-context__cell'
						key={f.label}
					>
						<span className='vsr-context__cell-label'>{f.label}</span>
						<span className={`vsr-context__cell-value ${f.mono ? 'is-mono' : ''}`}>
							{f.value || '—'}
							{f.copy && f.value ? (
								<CopyValue
									value={f.value}
									label={f.label.toLowerCase()}
								/>
							) : null}
						</span>
					</div>
				))}
		</div>
	</div>
);

// ── Turnout progress ring ──
export const ProgressRing = ({ value, caption }: { value: number | null; caption: string }) => {
	const pct = value == null ? 0 : Math.min(Math.max(value * 100, 0), 100);
	const radius = 34;
	const circumference = 2 * Math.PI * radius;
	const offset = circumference - (pct / 100) * circumference;
	return (
		<div className='vsr-ring'>
			<svg
				viewBox='0 0 80 80'
				className='vsr-ring__svg'
			>
				<circle
					className='vsr-ring__track'
					cx='40'
					cy='40'
					r={radius}
					fill='none'
					strokeWidth='7'
				/>
				<circle
					className='vsr-ring__value'
					cx='40'
					cy='40'
					r={radius}
					fill='none'
					strokeWidth='7'
					strokeLinecap='round'
					strokeDasharray={circumference}
					strokeDashoffset={offset}
					transform='rotate(-90 40 40)'
				/>
			</svg>
			<span className='vsr-ring__label'>{value == null ? '—' : `${Math.round(pct)}%`}</span>
			<span className='vsr-ring__caption'>{caption}</span>
		</div>
	);
};

// ── The 7 recorded summary numbers ──
export const SummaryGrid = ({ result }: { result: IIRevResultState }) => {
	const rows: { label: string; value?: number }[] = [
		{ label: 'Registered voters', value: result.votersOnRegister },
		{ label: 'Accredited voters', value: result.accreditedVoters },
		{ label: 'Ballots issued', value: result.ballotPapersIssuedToPoolingUnit },
		{ label: 'Unused ballots', value: result.unusedBallotPapers },
		{ label: 'Rejected ballots', value: result.rejectedBallot },
		{ label: 'Total used ballots', value: result.totalUsedBallotPapers },
		{ label: 'Valid votes', value: result.totalValidVotes },
	];
	return (
		<div className='vsr-section'>
			<span className='ca-result__section-title'>Recorded figures</span>
			<div className='ca-result__grid'>
				{rows.map((r) => (
					<div
						className='ca-result__cell'
						key={r.label}
					>
						<span className='ca-result__cell-label'>{r.label}</span>
						<span className='ca-result__cell-value'>{fmtNum(r.value)}</span>
					</div>
				))}
			</div>
		</div>
	);
};

// ── Derived analytics matrix ──
export const KpiMatrix = ({ kpis }: { kpis: IDerivedKpis }) => {
	const leaderLabel = kpis.leader?.partyName || (kpis.leader ? `Party ${kpis.leader.partyId}` : '—');
	const tiles: { label: string; value: string; hint?: string; tone?: 'success' | 'warning' }[] = [
		{ label: 'Valid-vote rate', value: asPercent(kpis.validVoteRate), hint: 'of accredited voters' },
		{ label: 'Rejection rate', value: asPercent(kpis.rejectionRate), hint: 'of valid votes', tone: 'warning' },
		{ label: 'Ballot utilisation', value: asPercent(kpis.ballotUtilisation), hint: 'used of issued' },
		{
			label: 'Leading party',
			value: leaderLabel,
			hint: kpis.leadMargin != null ? `+${asPercent(kpis.leadMargin)} margin` : undefined,
			tone: 'success',
		},
	];
	return (
		<div className='vsr-section'>
			<span className='ca-result__section-title'>Analytics</span>
			<div className='vsr-matrix'>
				<div className='vsr-matrix__ring'>
					<ProgressRing
						value={kpis.turnout}
						caption='Voter turnout'
					/>
				</div>
				<div className='vsr-matrix__tiles'>
					{tiles.map((t) => (
						<div
							className={`vsr-kpi ${t.tone ? `vsr-kpi--${t.tone}` : ''}`}
							key={t.label}
						>
							<span className='vsr-kpi__label'>{t.label}</span>
							<span className='vsr-kpi__value'>{t.value}</span>
							{t.hint ? <span className='vsr-kpi__hint'>{t.hint}</span> : null}
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

// ── Color-coded party-vote bars (mirrors child-agent PartyBars) ──
const partyName = (p: IResult) => p.partyName || `Party ${p.partyId}`;

export const PartyBars = ({ result, kpis }: { result: IIRevResultState; kpis: IDerivedKpis }) => {
	const parties = sortedParties(result);
	if (!parties.length) return null;
	const max = parties[0]?.votes || 1;
	const leader = parties[0];
	const leaderColor = getPartyColor(partyName(leader));

	return (
		<div className='vsr-section'>
			<div className='ca-result__parties-head'>
				<span className='ca-result__section-title'>Party votes</span>
				<span
					className='ca-result__leader'
					style={{ background: leaderColor.text, color: leaderColor.bg }}
				>
					Leading · {partyName(leader)} · {fmtNum(leader.votes)}
				</span>
			</div>
			<ul className='ca-result__bars'>
				{parties.map((p) => {
					const color = getPartyColor(partyName(p));
					const pct = Math.max(((p.votes ?? 0) / max) * 100, 2);
					return (
						<li
							className='ca-result__bar-row'
							key={`${p.partyId}-${p.partyName}`}
						>
							<span
								className='ca-result__bar-tag'
								style={{ background: color.bg, color: color.text }}
							>
								{partyName(p)}
							</span>
							<span className='ca-result__bar-track'>
								<span
									className='ca-result__bar-fill'
									style={{ width: `${pct}%`, background: color.bg }}
								/>
							</span>
							<span className='ca-result__bar-votes'>{fmtNum(p.votes)}</span>
						</li>
					);
				})}
			</ul>
			{kpis.mismatch ? (
				<p className='ca-result__mismatch'>
					<WarnGlyph />
					Party votes total {fmtNum(kpis.tallied)}, but the recorded valid votes are {fmtNum(result.totalValidVotes)}.
				</p>
			) : null}
		</div>
	);
};
